import axios, { AxiosInstance, AxiosError } from 'axios';
import { useAuthStore } from '../stores/auth';
import { getCurrentSubdomain } from './subdomain';
import { getCsrfToken } from './csrf';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';

const UNSAFE_METHODS = new Set(['post', 'put', 'patch', 'delete']);

// Create axios instance
const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add tenant subdomain + CSRF headers
apiClient.interceptors.request.use(
  config => {
    const subdomain = getCurrentSubdomain();
    if (subdomain) {
      config.headers['X-Tenant-Subdomain'] = subdomain;
    }

    // Double-submit CSRF token for mutating requests, matched against the
    // CsrfGuard check on the backend (auth cookies run SameSite=None).
    if (config.method && UNSAFE_METHODS.has(config.method)) {
      const csrfToken = getCsrfToken();
      if (csrfToken) {
        config.headers['X-CSRF-Token'] = csrfToken;
      }
    }

    return config;
  },
  error => Promise.reject(error)
);

// Response interceptor to handle token refresh
apiClient.interceptors.response.use(
  response => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as any;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const authStore = useAuthStore();
        await authStore.refreshAccessToken();
        return apiClient(originalRequest);
      } catch (refreshError) {
        const authStore = useAuthStore();
        authStore.clearAuthState();
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

// Auth API (tenant-specific)
export const authApi = {
  async login(credentials: { login: string; password: string }) {
    const response = await apiClient.post('/auth/tenant/login', credentials);
    return response.data;
  },

  async logout() {
    const response = await apiClient.post('/auth/tenant/logout');
    return response.data;
  },

  async refreshToken() {
    const response = await apiClient.post('/auth/tenant/refresh');
    return response.data;
  },

  async getCurrentUser() {
    const response = await apiClient.get('/auth/tenant/me');
    return response.data;
  },

  async setupPassword(data: { token: string; password: string; confirmPassword: string }) {
    const response = await apiClient.post('/auth/password-setup', {
      token: data.token,
      password: data.password,
      confirmPassword: data.confirmPassword,
    });
    return response.data;
  },

  async requestPasswordReset(email: string) {
    const response = await apiClient.post('/auth/password-reset/request', { email });
    return response.data;
  },

  async resetPassword(token: string, password: string) {
    const response = await apiClient.post('/auth/password-reset/reset', { token, password });
    return response.data;
  },
};

// Tenant API
export const tenantApi = {
  async validateSubdomain(subdomain: string) {
    const response = await apiClient.get(`/tenants/validate-subdomain/${subdomain}`);
    return response.data;
  },
};

// Platform Settings API (for domain configuration)
export const platformSettingsApi = {
  async getSettings() {
    const response = await apiClient.get('/platform-settings');
    return response.data;
  },
};

// Public admission application (no auth; tenant from subdomain)
export const publicAdmissionApi = {
  async getOptions() {
    const response = await apiClient.get('/public/admission/options');
    return response.data;
  },

  async submit(data: {
    childFirstName: string;
    childLastName: string;
    childDateOfBirth: string;
    childGender: string;
    childEmail: string;
    childPhone?: string;
    childAddress?: string;
    guardianName: string;
    guardianPhone?: string;
    guardianEmail: string;
    guardianRelation?: string;
    desiredCurriculumId?: string;
    desiredGradeCode?: string;
    desiredPathwayId?: string;
    desiredIntakeId?: string;
    previousSchool?: string;
    previousSchoolAddress?: string;
    previousSchoolContact?: string;
    previousSchoolYearLeft?: string;
    previousAssessmentScore?: string;
    supportNeeds?: {
      visual?: boolean;
      mobility?: boolean;
      hearing?: boolean;
      learning?: boolean;
      otherNotes?: string;
    };
  }) {
    const response = await apiClient.post('/public/admission/submit', data);
    return response.data;
  },

  async track(data: {
    trackingNumber: string;
    childFirstName?: string;
    childDateOfBirth?: string;
  }) {
    const response = await apiClient.post('/public/admission/track', data);
    return response.data;
  },
};

// Admission applications (school portal – authenticated)
export const admissionApplicationsApi = {
  async getStats() {
    const response = await apiClient.get('/admission-applications/stats');
    return response.data;
  },

  async findAll(params?: { status?: string; search?: string }) {
    const response = await apiClient.get('/admission-applications', { params });
    return response.data?.data ?? response.data;
  },

  async findOne(id: string) {
    const response = await apiClient.get(`/admission-applications/${id}`);
    return response.data;
  },

  async process(
    id: string,
    data: {
      action: string;
      rejectionReason?: string;
      assignedAdmissionNumber?: string;
      assignedClassId?: string;
      assignedAcademicYearId?: string;
    }
  ) {
    const response = await apiClient.patch(`/admission-applications/${id}/process`, data);
    return response.data;
  },
};

// Students API
export const studentsApi = {
  async getStats() {
    const response = await apiClient.get('/students/stats');
    return response.data;
  },

  async findAll(params?: {
    classId?: string;
    status?: string;
    search?: string;
    page?: number;
    limit?: number;
  }) {
    const response = await apiClient.get('/students', { params });
    return response.data;
  },

  async findOne(id: string) {
    const response = await apiClient.get(`/students/${id}`);
    return response.data;
  },

  async getAvailableClasses(dateOfBirth: string, academicYearId: string) {
    const response = await apiClient.get('/students/available-classes', {
      params: { dateOfBirth, academicYearId },
    });
    return response.data;
  },

  async create(data: {
    firstName: string;
    lastName: string;
    email: string;
    phone?: string;
    admissionNumber: string;
    dateOfBirth: string;
    gender: 'male' | 'female' | 'other';
    bloodGroup?: string;
    address?: string;
    enrollmentDate: string;
    currentClassId?: string;
    guardianName?: string;
    guardianPhone?: string;
    guardianEmail?: string;
    guardianRelation?: string;
    previousSchool?: string;
    previousSchoolAddress?: string;
    previousSchoolContact?: string;
    previousSchoolYearLeft?: string;
    previousAssessmentScore?: string;
    notes?: string;
    medicalInfo?: Record<string, any>;
    metadata?: Record<string, any>;
    ageOverride?: boolean;
  }) {
    const response = await apiClient.post('/students', data);
    return response.data;
  },

  async update(
    id: string,
    data: {
      firstName?: string;
      lastName?: string;
      email?: string;
      phone?: string;
      admissionNumber?: string;
      dateOfBirth?: string;
      gender?: 'male' | 'female' | 'other';
      bloodGroup?: string;
      address?: string;
      enrollmentDate?: string;
      currentClassId?: string;
      guardianName?: string;
      guardianPhone?: string;
      guardianEmail?: string;
      guardianRelation?: string;
      previousSchool?: string;
      previousSchoolAddress?: string;
      previousSchoolContact?: string;
      previousSchoolYearLeft?: string;
      previousAssessmentScore?: string;
      notes?: string;
      medicalInfo?: Record<string, any>;
      metadata?: Record<string, any>;
      ageOverride?: boolean;
    }
  ) {
    const response = await apiClient.patch(`/students/${id}`, data);
    return response.data;
  },

  async delete(id: string) {
    const response = await apiClient.delete(`/students/${id}`);
    return response.data;
  },

  async getAuditLogs(id: string) {
    const response = await apiClient.get(`/students/${id}/audit-logs`);
    return response.data;
  },

  async bulkUpload(file: File) {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post('/students/bulk-upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },
};

// Staff API
export const staffApi = {
  async findAll(params?: any) {
    const response = await apiClient.get('/staff', { params });
    return response.data;
  },

  async findOne(id: string) {
    const response = await apiClient.get(`/staff/${id}`);
    return response.data;
  },

  async checkUsername(username: string): Promise<{ available: boolean }> {
    const response = await apiClient.get('/staff/check-username', {
      params: { username: username?.trim() || '' },
    });
    return response.data;
  },

  async create(data: any) {
    const response = await apiClient.post('/staff', data);
    return response.data;
  },

  async update(id: string, data: any) {
    const response = await apiClient.patch(`/staff/${id}`, data);
    return response.data;
  },

  async resetPassword(
    id: string
  ): Promise<{ temporaryPassword: string; emailSent: boolean; sentTo?: string }> {
    const response = await apiClient.post(`/staff/${id}/reset-password`);
    return response.data;
  },

  async delete(id: string) {
    const response = await apiClient.delete(`/staff/${id}`);
    return response.data;
  },

  async bulkUpload(file: File) {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post('/staff/bulk-upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },
};

// Classes API (old methods - kept for backward compatibility)
// Note: New methods are defined later in the file

// Customization API
export const customizationApi = {
  async get() {
    const response = await apiClient.get('/customization');
    return response.data;
  },

  async getPublic(subdomain: string) {
    const response = await apiClient.get(`/customization/public/${subdomain}`);
    return response.data;
  },

  async update(data: {
    slogan?: string;
    welcomeMessage?: string;
    primaryColor?: string;
    secondaryColor?: string;
    accentColor?: string;
    headerBgColor?: string;
    sidebarBgColor?: string;
    textPrimaryColor?: string;
    textSecondaryColor?: string;
    loginBgType?: string;
    loginBgColor?: string;
    loginBgGradient?: string;
    showSloganOnLogin?: boolean;
    headerFont?: string;
    bodyFont?: string;
    emailFooterText?: string;
    emailPrimaryColor?: string;
    facebookUrl?: string;
    twitterUrl?: string;
    instagramUrl?: string;
    linkedinUrl?: string;
    youtubeUrl?: string;
    websiteUrl?: string;
    applyPageHeaderBgColor?: string;
    applyPageLogoPosition?: 'left' | 'right';
    applyPageHeaderText?: string;
  }) {
    const response = await apiClient.put('/customization', data);
    return response.data;
  },

  async uploadLogo(
    type: 'primary' | 'icon' | 'login' | 'email' | 'favicon' | 'login-bg',
    file: File
  ) {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post(`/customization/logo/${type}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  async deleteLogo(type: 'primary' | 'icon' | 'login' | 'email' | 'favicon' | 'login-bg') {
    const response = await apiClient.delete(`/customization/logo/${type}`);
    return response.data;
  },

  async reset() {
    const response = await apiClient.post('/customization/reset');
    return response.data;
  },
};

// School Info API
export const schoolInfoApi = {
  async get() {
    const response = await apiClient.get('/school-info');
    return response.data;
  },

  async update(data: {
    name: string;
    tagline: string;
    streetAddress: string;
    city: string;
    state?: string;
    country: string;
    postalCode?: string;
    schoolCode?: string;
    establishedYear?: number;
    schoolType?: string;
    curriculum?: string;
    phoneNumber?: string;
    phoneNumberSecondary?: string;
    email?: string;
    website?: string;
    principalName?: string;
    registrationNumber?: string;
    taxId?: string;
    branchInheritance?: 'shared' | 'separate';
  }) {
    const response = await apiClient.put('/school-info', data);
    return response.data;
  },

  async getBranches() {
    const response = await apiClient.get('/school-info/branches');
    return response.data;
  },

  async getBranchInfo(branchId: string) {
    const response = await apiClient.get(`/school-info/branches/${branchId}`);
    return response.data;
  },

  async updateBranchInfo(
    branchId: string,
    data: {
      name?: string;
      tagline?: string;
      streetAddress?: string;
      city?: string;
      state?: string;
      country?: string;
      postalCode?: string;
      phoneNumber?: string;
      phoneNumberSecondary?: string;
      email?: string;
      principalName?: string;
    }
  ) {
    const response = await apiClient.put(`/school-info/branches/${branchId}`, data);
    return response.data;
  },

  async getEffective() {
    const response = await apiClient.get('/school-info/effective');
    return response.data;
  },
};

// Notification Settings API
export const notificationSettingsApi = {
  // Settings
  async getSettings() {
    const response = await apiClient.get('/notification-settings');
    return response.data;
  },

  async updateEmailSettings(data: {
    smtpHost?: string;
    smtpPort?: number;
    smtpUsername?: string;
    smtpPassword?: string;
    smtpEncryption?: 'none' | 'ssl' | 'tls';
    emailFromAddress?: string;
    emailFromName?: string;
    emailEnabled?: boolean;
  }) {
    const response = await apiClient.put('/notification-settings/email', data);
    return response.data;
  },

  async updateSmsSettings(data: {
    smsProvider?: 'excelient' | 'twilio' | 'africastalking' | 'nexmo' | 'messagebird';
    smsApiKey?: string;
    smsApiSecret?: string;
    smsSenderId?: string;
    smsPartnerId?: string;
    smsApiUrl?: string;
    smsEnabled?: boolean;
  }) {
    const response = await apiClient.put('/notification-settings/sms', data);
    return response.data;
  },

  async testEmail(recipientEmail: string) {
    const response = await apiClient.post('/notification-settings/test-email', { recipientEmail });
    return response.data;
  },

  async testSms(recipientPhone: string) {
    const response = await apiClient.post('/notification-settings/test-sms', { recipientPhone });
    return response.data;
  },

  // Preferences
  async getPreferences() {
    const response = await apiClient.get('/notification-settings/preferences');
    return response.data;
  },

  async updatePreference(data: {
    eventType: string;
    emailEnabled?: boolean;
    smsEnabled?: boolean;
    inAppEnabled?: boolean;
    reminderDaysBefore?: number;
    reminderTime?: string;
    notifyParents?: boolean;
    notifyStudents?: boolean;
    notifyStaff?: boolean;
  }) {
    const response = await apiClient.put('/notification-settings/preferences', data);
    return response.data;
  },

  // Templates
  async getTemplates() {
    const response = await apiClient.get('/notification-settings/templates');
    return response.data;
  },

  async getTemplate(eventType: string, channel: string) {
    const response = await apiClient.get(
      `/notification-settings/templates/${eventType}/${channel}`
    );
    return response.data;
  },

  async updateTemplate(data: {
    eventType: string;
    channel: 'email' | 'sms';
    subject?: string;
    body: string;
    isActive?: boolean;
  }) {
    const response = await apiClient.put('/notification-settings/templates', data);
    return response.data;
  },

  async getDefaultTemplate(eventType: string, channel: string) {
    const response = await apiClient.get(
      `/notification-settings/templates/default/${eventType}/${channel}`
    );
    return response.data;
  },
};

// Tenant Email API (for school portal)
export const securityApi = {
  // Permissions
  async getPermissions() {
    const response = await apiClient.get('/security/permissions');
    return response.data;
  },

  // Roles
  async getRoles() {
    const response = await apiClient.get('/security/roles');
    return response.data;
  },

  async getRole(id: string) {
    const response = await apiClient.get(`/security/roles/${id}`);
    return response.data;
  },

  async createRole(data: {
    name: string;
    description?: string;
    permissions: string[];
    isActive?: boolean;
  }) {
    const response = await apiClient.post('/security/roles', data);
    return response.data;
  },

  async updateRole(
    id: string,
    data: { name?: string; description?: string; permissions?: string[]; isActive?: boolean }
  ) {
    const response = await apiClient.put(`/security/roles/${id}`, data);
    return response.data;
  },

  async deleteRole(id: string) {
    const response = await apiClient.delete(`/security/roles/${id}`);
    return response.data;
  },

  // Groups
  async getGroups() {
    const response = await apiClient.get('/security/groups');
    return response.data;
  },

  async getGroup(id: string) {
    const response = await apiClient.get(`/security/groups/${id}`);
    return response.data;
  },

  async createGroup(data: { name: string; description?: string }) {
    const response = await apiClient.post('/security/groups', data);
    return response.data;
  },

  async updateGroup(id: string, data: { name?: string; description?: string; isActive?: boolean }) {
    const response = await apiClient.put(`/security/groups/${id}`, data);
    return response.data;
  },

  async deleteGroup(id: string) {
    const response = await apiClient.delete(`/security/groups/${id}`);
    return response.data;
  },

  async assignRolesToGroup(id: string, roleIds: string[]) {
    const response = await apiClient.post(`/security/groups/${id}/roles`, { roleIds });
    return response.data;
  },

  // Users
  async getUsers() {
    const response = await apiClient.get('/security/users');
    return response.data;
  },

  async getUser(id: string) {
    const response = await apiClient.get(`/security/users/${id}`);
    return response.data;
  },

  async createUser(data: {
    email: string;
    password: string;
    firstName: string;
    lastName: string;
    phone?: string;
    userType: string;
    isActive?: boolean;
    groupIds?: string[];
    roleIds?: string[];
  }) {
    const response = await apiClient.post('/security/users', data);
    return response.data;
  },

  async updateUser(
    id: string,
    data: {
      email?: string;
      password?: string;
      firstName?: string;
      lastName?: string;
      phone?: string;
      userType?: string;
      isActive?: boolean;
    }
  ) {
    const response = await apiClient.put(`/security/users/${id}`, data);
    return response.data;
  },

  async deleteUser(id: string) {
    const response = await apiClient.delete(`/security/users/${id}`);
    return response.data;
  },

  async assignGroupsToUser(id: string, groupIds: string[]) {
    const response = await apiClient.post(`/security/users/${id}/groups`, { groupIds });
    return response.data;
  },

  async assignRolesToUser(id: string, roleIds: string[]) {
    const response = await apiClient.post(`/security/users/${id}/roles`, { roleIds });
    return response.data;
  },

  // Direct Permissions
  async assignPermissionsToUser(userId: string, permissionCodes: string[]) {
    const response = await apiClient.post(`/security/users/${userId}/permissions`, {
      permissionCodes,
    });
    return response.data;
  },

  async getUserPermissions(userId: string): Promise<string[]> {
    const response = await apiClient.get(`/security/users/${userId}/permissions`);
    return response.data;
  },
};

export const emailApi = {
  // Email Account
  async createAccount(data: {
    username: string;
    password: string;
    firstname?: string;
    lastname?: string;
  }) {
    const response = await apiClient.post('/tenant-email/account', data);
    return response.data;
  },

  async connectAccount(data: { username: string; password: string }) {
    const response = await apiClient.post('/tenant-email/account/connect', data);
    return response.data;
  },

  async getAccount() {
    const response = await apiClient.get('/tenant-email/account');
    return response.data;
  },

  async refreshToken(data?: { password?: string }) {
    const response = await apiClient.post('/tenant-email/account/refresh-token', data || {});
    return response.data;
  },

  // SMTP Configs
  async createSmtpConfig(data: {
    host: string;
    port: number;
    username: string;
    password: string;
    useTls: boolean;
    fromEmail?: string;
    fromName?: string;
    replyTo?: string;
    isDefault?: boolean;
    lemConfigId?: string;
  }) {
    const response = await apiClient.post('/tenant-email/smtp-config', data);
    return response.data;
  },

  async getSmtpConfigs() {
    const response = await apiClient.get('/tenant-email/smtp-configs');
    return response.data;
  },

  async getDefaultSmtpConfig() {
    const response = await apiClient.get('/tenant-email/smtp-config/default');
    return response.data;
  },

  async getSmtpConfig(id: string) {
    const response = await apiClient.get(`/tenant-email/smtp-config/${id}`);
    return response.data;
  },

  async updateSmtpConfig(
    id: string,
    data: {
      host?: string;
      port?: number;
      username?: string;
      password?: string;
      useTls?: boolean;
      fromEmail?: string;
      fromName?: string;
      replyTo?: string;
    }
  ) {
    const response = await apiClient.put(`/tenant-email/smtp-config/${id}`, data);
    return response.data;
  },

  async deleteSmtpConfig(id: string) {
    const response = await apiClient.delete(`/tenant-email/smtp-config/${id}`);
    return response.data;
  },

  async setDefaultSmtpConfig(id: string) {
    const response = await apiClient.patch(`/tenant-email/smtp-config/${id}/set-default`);
    return response.data;
  },

  async getLemuSmtpConfigs() {
    const response = await apiClient.get('/tenant-email/smtp-configs/lemu');
    return response.data;
  },

  async testSmtpConfig(id: string, data: { to: string }) {
    const response = await apiClient.post(`/tenant-email/smtp-config/${id}/test`, data);
    return response.data;
  },

  // Send Email
  async sendEmail(data: {
    to: string[];
    subject: string;
    body: string;
    cc?: string[];
    bcc?: string[];
    replyTo?: string;
    attachments?: Array<{ filename: string; content: string; contentType?: string }>;
  }) {
    const response = await apiClient.post('/tenant-email/send', data);
    return response.data;
  },

  async getEmailStatus(requestId: string) {
    const response = await apiClient.get(`/tenant-email/status/${requestId}`);
    return response.data;
  },

  // Email Logs
  async getEmailLogs(params?: { status?: string; limit?: number; offset?: number }) {
    const response = await apiClient.get('/tenant-email/logs', { params });
    return response.data;
  },

  // Platform SMTP Config (for tenant)
  async getPlatformSmtpConfigs() {
    const response = await apiClient.get('/tenant-email/platform-smtp-configs');
    return response.data;
  },

  async createPlatformSmtpConfig(data: {
    host: string;
    port: number;
    username: string;
    password: string;
    useTls: boolean;
    fromEmail?: string;
    fromName?: string;
    replyTo?: string;
    isDefault?: boolean;
    lemConfigId?: string;
  }) {
    const response = await apiClient.post('/tenant-email/platform-smtp-config', data);
    return response.data;
  },

  async updatePlatformSmtpConfig(
    id: string,
    data: {
      host?: string;
      port?: number;
      username?: string;
      password?: string;
      useTls?: boolean;
      fromEmail?: string;
      fromName?: string;
      replyTo?: string;
      isDefault?: boolean;
    }
  ) {
    const response = await apiClient.put(`/tenant-email/platform-smtp-config/${id}`, data);
    return response.data;
  },

  async testPlatformSmtpConfig(id: string, data: { to: string }) {
    const response = await apiClient.post(`/tenant-email/platform-smtp-config/${id}/test`, data);
    return response.data;
  },
};

// Billing API
export const billingApi = {
  // Get current tenant's subscription info
  async getSubscription() {
    const response = await apiClient.get('/billing/subscription');
    return response.data;
  },

  // Get current tenant's invoices
  async getInvoices(params?: { status?: string; page?: number; limit?: number }) {
    const response = await apiClient.get('/billing/invoices', { params });
    return response.data;
  },

  // Get a specific invoice
  async getInvoice(id: string) {
    const response = await apiClient.get(`/billing/invoices/${id}`);
    return response.data;
  },

  // Get invoice PDF
  async getInvoicePdf(id: string) {
    const response = await apiClient.get(`/billing/invoices/${id}/pdf`);
    return response.data;
  },

  // Get payments by invoice
  async getPaymentsByInvoice(invoiceId: string) {
    const response = await apiClient.get(`/billing/payments/invoice/${invoiceId}`);
    return response.data;
  },

  // Get current tenant's payments
  async getPayments(params?: { page?: number; limit?: number }) {
    const response = await apiClient.get('/billing/payments', { params });
    return response.data;
  },

  // Get billing plans (for viewing available plans)
  async getPlans(activeOnly = true) {
    const response = await apiClient.get('/billing/plans', {
      params: { activeOnly },
    });
    return response.data;
  },
};

// Curriculum API (Tenant)
export const curriculumApi = {
  async getAvailableCurricula() {
    const response = await apiClient.get('/curriculum/available');
    return response.data;
  },

  async getCurrentCurriculum() {
    const response = await apiClient.get('/curriculum/current');
    return response.data;
  },

  async getSyncStatus() {
    const response = await apiClient.get('/curriculum/sync-status');
    return response.data;
  },

  async getSyncPreview() {
    const response = await apiClient.get('/curriculum/sync-preview');
    return response.data;
  },

  async syncCurriculum(preview = false) {
    const response = await apiClient.post('/curriculum/sync', { preview });
    return response.data;
  },

  async getGrades(curriculumId?: string) {
    const params: Record<string, string> = {};
    if (curriculumId && curriculumId.trim()) params.curriculumId = curriculumId;
    const response = await apiClient.get('/curriculum/grades', { params });
    return response.data;
  },

  async getSubjects(gradeId?: string, curriculumId?: string) {
    const params: Record<string, string> = {};
    if (gradeId) params.gradeId = gradeId;
    if (curriculumId) params.curriculumId = curriculumId;
    const response = await apiClient.get('/curriculum/subjects', { params });
    return response.data;
  },

  async getPathways(gradeId: string, curriculumId?: string) {
    const params: Record<string, string> = { gradeId };
    if (curriculumId) params.curriculumId = curriculumId;
    const response = await apiClient.get('/curriculum/pathways', { params });
    return response.data;
  },

  async getPathwaySubjects(pathwayId: string) {
    const response = await apiClient.get(`/curriculum/pathways/${pathwayId}/subjects`);
    return response.data;
  },

  async getOverrides() {
    const response = await apiClient.get('/curriculum/overrides');
    return response.data;
  },

  async createOverride(data: {
    overrideType: string;
    platformSubjectId?: string;
    platformGradeId?: string;
    customName?: string;
    customCode?: string;
    isActive?: boolean;
    customMetadata?: Record<string, any>;
  }) {
    const response = await apiClient.post('/curriculum/overrides', data);
    return response.data;
  },

  async updateOverride(
    id: string,
    data: {
      customName?: string;
      customCode?: string;
      isActive?: boolean;
      customMetadata?: Record<string, any>;
    }
  ) {
    const response = await apiClient.put(`/curriculum/overrides/${id}`, data);
    return response.data;
  },

  async deleteOverride(id: string) {
    const response = await apiClient.delete(`/curriculum/overrides/${id}`);
    return response.data;
  },
};

// Classes API
export const classesApi = {
  async getAll(
    academicYearId?: string,
    status?: string,
    params?: { page?: number; limit?: number; search?: string }
  ) {
    const response = await apiClient.get('/classes', {
      params: {
        academicYearId,
        status,
        page: params?.page,
        limit: params?.limit,
        search: params?.search?.trim() || undefined,
      },
    });
    return response.data as {
      data: any[];
      total: number;
      page: number;
      limit: number;
      totalPages: number;
    };
  },

  async getById(id: string) {
    const response = await apiClient.get(`/classes/${id}`);
    return response.data;
  },

  async getAvailableLevels(academicYearId: string) {
    const response = await apiClient.get('/classes/available-levels', {
      params: { academicYearId },
    });
    return response.data;
  },

  async getCurricula(academicYearId: string) {
    const response = await apiClient.get('/classes/curricula', {
      params: { academicYearId },
    });
    return response.data;
  },

  async create(data: {
    name: string;
    level: string;
    stream?: string;
    academicYearId: string;
    branchId?: string;
    capacity?: number;
    classTeacherId?: string;
    roomNumber?: string;
    curriculumId?: string;
    metadata?: Record<string, any>;
    subjectIds?: string[];
  }) {
    const response = await apiClient.post('/classes', data);
    return response.data;
  },

  async update(
    id: string,
    data: {
      name?: string;
      level?: string;
      stream?: string;
      branchId?: string;
      capacity?: number;
      classTeacherId?: string;
      roomNumber?: string;
      subjectIds?: string[];
      metadata?: Record<string, any>;
    }
  ) {
    const response = await apiClient.patch(`/classes/${id}`, data);
    return response.data;
  },

  async delete(id: string) {
    const response = await apiClient.delete(`/classes/${id}`);
    return response.data;
  },

  async archive(id: string) {
    const response = await apiClient.patch(`/classes/${id}/archive`);
    return response.data;
  },
};

// Timetable API
export const timetableApi = {
  async getAll(classId?: string, academicYearId?: string) {
    const response = await apiClient.get('/timetable', { params: { classId, academicYearId } });
    return response.data;
  },

  async create(data: {
    classId: string;
    academicYearId: string;
    dayOfWeek: number;
    periodNumber: number;
    subjectId: string;
    staffId: string;
    roomNumber?: string;
    startTime: string;
    endTime: string;
  }) {
    const response = await apiClient.post('/timetable', data);
    return response.data;
  },

  async update(
    id: string,
    data: {
      dayOfWeek?: number;
      periodNumber?: number;
      subjectId?: string;
      staffId?: string;
      roomNumber?: string;
      startTime?: string;
      endTime?: string;
      status?: string;
    }
  ) {
    const response = await apiClient.patch(`/timetable/${id}`, data);
    return response.data;
  },

  async delete(id: string) {
    const response = await apiClient.delete(`/timetable/${id}`);
    return response.data;
  },
};

// Subjects API
export const subjectsApi = {
  async getAll(academicYearId?: string, status?: string) {
    const response = await apiClient.get('/subjects', {
      params: { academicYearId, status },
    });
    return response.data;
  },

  async getById(id: string) {
    const response = await apiClient.get(`/subjects/${id}`);
    return response.data;
  },

  async getAvailable(academicYearId: string, gradeId?: string) {
    const response = await apiClient.get('/subjects/available', {
      params: { academicYearId, gradeId },
    });
    return response.data;
  },

  async create(data: {
    name: string;
    code: string;
    description?: string;
    academicYearId: string;
    isCompulsory?: boolean;
    maxScore?: number;
    metadata?: Record<string, any>;
    curriculumSubjectId?: string;
  }) {
    const response = await apiClient.post('/subjects', data);
    return response.data;
  },

  async update(
    id: string,
    data: {
      name?: string;
      code?: string;
      description?: string;
      isCompulsory?: boolean;
      maxScore?: number;
      metadata?: Record<string, any>;
    }
  ) {
    const response = await apiClient.patch(`/subjects/${id}`, data);
    return response.data;
  },

  async delete(id: string) {
    const response = await apiClient.delete(`/subjects/${id}`);
    return response.data;
  },

  async archive(id: string) {
    const response = await apiClient.patch(`/subjects/${id}/archive`);
    return response.data;
  },
};

export const academicYearsApi = {
  async getAll(branchId?: string, status?: string) {
    const response = await apiClient.get('/academic-years', {
      params: { branchId, status },
    });
    return response.data;
  },

  async getCurrent(branchId?: string) {
    const response = await apiClient.get('/academic-years/current', {
      params: { branchId },
    });
    return response.data;
  },

  async getById(id: string) {
    const response = await apiClient.get(`/academic-years/${id}`);
    return response.data;
  },

  async create(data: {
    name: string;
    startDate: string;
    endDate: string;
    branchIds?: string[];
    isCurrent?: boolean;
    status?: string;
    curriculumId?: string;
    curriculumVersion?: string;
  }) {
    const response = await apiClient.post('/academic-years', data);
    return response.data;
  },

  async update(
    id: string,
    data: {
      name?: string;
      startDate?: string;
      endDate?: string;
      branchIds?: string[];
      isCurrent?: boolean;
      status?: string;
      curriculumId?: string;
      curriculumVersion?: string;
    }
  ) {
    const response = await apiClient.patch(`/academic-years/${id}`, data);
    return response.data;
  },

  async delete(id: string) {
    const response = await apiClient.delete(`/academic-years/${id}`);
    return response.data;
  },

  async archive(id: string) {
    const response = await apiClient.patch(`/academic-years/${id}/archive`);
    return response.data;
  },

  async getAvailablePlatformAcademicYears(curriculumId?: string) {
    const response = await apiClient.get('/academic-years/platform/available', {
      params: { curriculumId },
    });
    return response.data;
  },

  async syncFromPlatform(platformAcademicYearId: string, branchIds?: string[]) {
    const response = await apiClient.post(
      `/academic-years/platform/sync/${platformAcademicYearId}`,
      { branchIds }
    );
    return response.data;
  },

  async resyncFromPlatform(academicYearId: string) {
    const response = await apiClient.post(`/academic-years/${academicYearId}/resync`);
    return response.data;
  },
};

export const policiesApi = {
  async findAll(category?: string) {
    const response = await apiClient.get('/policies', { params: category ? { category } : {} });
    return response.data as any[];
  },

  async create(data: {
    key: string;
    name: string;
    description?: string;
    enabled?: boolean;
    category?: string;
  }) {
    const response = await apiClient.post('/policies', data);
    return response.data;
  },

  async update(
    id: string,
    data: { name?: string; description?: string; enabled?: boolean; category?: string }
  ) {
    const response = await apiClient.patch(`/policies/${id}`, data);
    return response.data;
  },

  async toggle(id: string, enabled: boolean) {
    const response = await apiClient.patch(`/policies/${id}/toggle`, { enabled });
    return response.data;
  },

  async remove(id: string) {
    await apiClient.delete(`/policies/${id}`);
  },
};

export const documentTemplatesApi = {
  async findAll() {
    const response = await apiClient.get('/document-templates');
    return response.data as any[];
  },

  async findByType(type: string) {
    const response = await apiClient.get('/document-templates/by-type', { params: { type } });
    return response.data;
  },

  async upsert(data: {
    type: string;
    name?: string;
    subject?: string;
    headerText?: string;
    bodyText?: string;
    footerText?: string;
    showLogo?: boolean;
  }) {
    const response = await apiClient.post('/document-templates/upsert', data);
    return response.data;
  },

  async resetToDefault(type: string) {
    const response = await apiClient.post(`/document-templates/reset/${type}`);
    return response.data;
  },

  downloadAdmissionLetter(applicationId: string): string {
    return `${apiClient.defaults.baseURL}/admission-applications/${applicationId}/admission-letter`;
  },
};

export default apiClient;
